// Decides what the operator is told after hiding or restoring a product.
//
// Hiding a color is two writes: the Shopify status flip, and the re-stamp of
// `theme.sibling_options` on every surviving sibling. If the second one does not
// land, the product is hidden but its siblings still advertise its color — a
// swatch linking to a handle that now 404s. Reporting a plain "Product hidden"
// over that is the thing we are trying not to do.
//
// But `noop` is NOT a failure: a legacy product never carried swatches, so there
// was genuinely nothing to re-stamp. Warning on it would fire on every benign
// archive, and a warning that cries wolf is one operators learn to ignore —
// which would blunt exactly the signal that matters.
//
// Extracted from StorePage so it can be tested: the client suite is pure-logic
// (src/lib/*.test.ts), and a field rename on either side of the console proxy
// would otherwise silently regress the operator signal with every test green.

export type SiblingOptionsResult = {
  restamped?: boolean;
  noop?: boolean;
  reason?: string;
  error?: string;
};

export type RestampNotice =
  | { level: 'success'; message: string }
  | { level: 'warning'; message: string; description: string };

export function restampNotice(
  sib: SiblingOptionsResult | undefined | null,
  restore: boolean,
): RestampNotice {
  const done = restore ? 'Product restored' : 'Product hidden';

  // Absent field => an older v4 that predates the re-stamp. Degrade to the
  // previous behavior rather than crying wolf; deploy order is not a defect.
  if (!sib) return { level: 'success', message: done };
  if (sib.restamped !== false) return { level: 'success', message: done };
  if (sib.noop) return { level: 'success', message: done };

  return {
    level: 'warning',
    message: `${done} — color swatches NOT updated`,
    description: `Siblings may still show this color. ${sib.error || sib.reason || ''}`.trim(),
  };
}
